import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import "./RowActions.css";

/**
 * A row's secondary actions, tucked behind one "More actions" control so the row itself
 * carries exactly one primary action in view (ADR 0009 §4 — "the approval queue goes from
 * three buttons to one").
 *
 * A DISCLOSURE, NOT AN ARIA MENU WIDGET. The full `role="menu"`/`role="menuitem"` pattern
 * demands roving tabindex and reachability by arrow keys ALONE — the right shape for a flat
 * list of one-shot commands, the wrong one for content that is itself a small form (a reject
 * reason, an hours split). So the trigger toggles a plain popover (the WAI-ARIA "Disclosure"
 * pattern): whatever is passed as `children` keeps its own native role, tab order and
 * keyboard handling — a `<button>` stays `role="button"`, a `<select>` still opens on
 * ArrowDown — and the disclosure adds only what native semantics do not already give it:
 * `aria-expanded`/`aria-controls` on the trigger, Escape-to-close, and closing on an outside
 * click.
 *
 * GENERAL-PURPOSE ON PURPOSE: this file knows nothing about time entries, pay runs, or any
 * other row shape. Any row that needs to hide secondary actions behind one control can reuse
 * it — pass whatever content the row wants closed by default.
 */
export function RowActions({
  label = "More actions",
  children,
  onOpenChange,
}: {
  /** The trigger's accessible name. Kept short — it is read on every row of a long queue. */
  label?: string;
  children: ReactNode;
  /** Fires whenever the disclosure opens or closes, including via Escape or an outside
   *  click — so a row can reset a half-filled child form (a reject reason typed then
   *  abandoned) rather than leave it silently waiting behind a closed trigger. */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function setOpen(next: boolean): void {
    setOpenState(next);
    onOpenChange?.(next);
  }

  // Outside click closes the panel — the standard expectation for any dropdown/disclosure,
  // and without it a farmer taps elsewhere on the queue and the open panel from a DIFFERENT
  // row is left floating over whatever they meant to look at next.
  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent): void {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function onPanelKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    // Focus returns to the control that opened this, same rule PageHeader-adjacent dialogs
    // in this app already follow: a keyboard user who closes a popover should not be
    // dropped back at the top of the document.
    triggerRef.current?.focus();
  }

  return (
    <span className="row-actions" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--ghost row-actions__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {open && (
        <div id={panelId} className="row-actions__panel" onKeyDown={onPanelKeyDown}>
          {children}
        </div>
      )}
    </span>
  );
}
